import {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  ReactNode,
} from 'react';

import { ThemeContextValue, ThemeVariant, ClientTheme } from '@type/theme';
import {
  getTheme,
  applyTheme,
  getSystemTheme,
  getStoredTheme,
  storeTheme,
  applyClientTheme,
  getStoredClientTheme,
  storeClientTheme,
  getStoredOrgBranding,
} from '../utils/themes';
import { orgBrandingToClientTheme } from '@utils/operatorThemes';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: ThemeVariant;
  defaultClientTheme?: ClientTheme | null;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light',
  defaultClientTheme = null,
}) => {
  const [themeVariant, setThemeVariantState] = useState<ThemeVariant>(() => {
    // Try to get stored theme first, then system preference, then default
    const storedTheme = getStoredTheme();
    if (storedTheme) return storedTheme;

    const systemTheme = getSystemTheme();
    return systemTheme || defaultTheme;
  });

  const [clientTheme, setClientThemeState] = useState<ClientTheme | null>(
    () => {
      // 1. A manually-selected theme wins.
      const storedClientTheme = getStoredClientTheme();
      if (storedClientTheme) return storedClientTheme;
      // 2. Otherwise seed from last-known-good org branding so the branded palette is applied
      //    before first paint, instead of flashing the default while the CMS config loads.
      const cachedBranding = getStoredOrgBranding();
      if (cachedBranding) return orgBrandingToClientTheme(cachedBranding);
      return defaultClientTheme;
    }
  );

  const [isThemeManuallySelected, setIsThemeManuallySelected] =
    useState<boolean>(() => {
      return getStoredClientTheme() !== null;
    });

  const theme = getTheme(themeVariant);

  const setThemeVariant = useCallback((variant: ThemeVariant) => {
    setThemeVariantState(variant);
    applyTheme(variant);
    storeTheme(variant);
  }, []);

  const setClientTheme = useCallback(
    (newClientTheme: ClientTheme | null, isManual = true) => {
      setClientThemeState(newClientTheme);
      applyClientTheme(newClientTheme);
      if (isManual) {
        storeClientTheme(newClientTheme);
        setIsThemeManuallySelected(true);
      } else {
        storeClientTheme(null);
        setIsThemeManuallySelected(false);
      }
    },
    []
  );

  const toggleTheme = useCallback(() => {
    setThemeVariantState(prev => {
      const newVariant = prev === 'light' ? 'dark' : 'light';
      applyTheme(newVariant);
      storeTheme(newVariant);
      return newVariant;
    });
  }, []);

  const resetToAutomaticTheme = useCallback(() => {
    setIsThemeManuallySelected(false);
    // Clear stored client theme to allow automatic selection
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('clientTheme');
    }
    // Reset client theme to null to trigger automatic selection
    setClientThemeState(null);
    applyClientTheme(null);
  }, []);

  // Apply theme on mount and when variant changes. useLayoutEffect so the CSS variables are set
  // before the browser paints — otherwise the first frame shows the unstyled default.
  useLayoutEffect(() => {
    applyTheme(themeVariant);
  }, [themeVariant]);

  // Apply client theme on mount and when it changes (before paint — see above).
  useLayoutEffect(() => {
    applyClientTheme(clientTheme);
  }, [clientTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if there's no stored theme preference
      const storedTheme = getStoredTheme();
      if (!storedTheme) {
        const systemTheme = e.matches ? 'dark' : 'light';
        setThemeVariantState(systemTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const contextValue: ThemeContextValue = {
    theme,
    themeVariant,
    clientTheme,
    isThemeManuallySelected,
    setThemeVariant,
    setClientTheme,
    toggleTheme,
    resetToAutomaticTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Re-export useTheme from hooks for backwards compatibility
export { useTheme } from '../hooks/useTheme';

export default ThemeContext;
